import { CLASS_COLORS } from "../../data/wowClasses";

const normalizeClassKey = (className) =>
  (className || "").replace(/\s+/g, "").toLowerCase();

const NORMALIZED_CLASS_COLORS = Object.entries(CLASS_COLORS).reduce(
  (acc, [name, color]) => {
    acc[normalizeClassKey(name)] = color;
    return acc;
  },
  {}
);

// WCL returns class names without spaces (e.g. "DeathKnight", "DemonHunter"),
// while CLASS_COLORS is keyed with spaces ("Death Knight") to match Blizzard's
// playable_class.name — normalize both sides so either source resolves.
export const getClassColor = (className, fallback = "#e5e7eb") =>
  NORMALIZED_CLASS_COLORS[normalizeClassKey(className)] || fallback;

export const DIFFICULTY_LABELS = {
  1: "LFR",
  3: "Normal",
  4: "Heroic",
  5: "Mythic",
};

export const getDifficultyLabel = (difficulty) =>
  DIFFICULTY_LABELS[difficulty] || (difficulty ? `Difficulty ${difficulty}` : "—");

export const ROLE_ICONS = {
  tank: "fas fa-shield-halved",
  healer: "fas fa-heart",
  dps: "fas fa-khanda",
};

export const getRoleIcon = (role) => ROLE_ICONS[role] || "fas fa-user";

const ROLE_COLORS = {
  tank: "text-blue-400",
  healer: "text-green-400",
  dps: "text-red-400",
};

export const getRoleColor = (role) => ROLE_COLORS[role] || "text-gray-400";

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

export const getRankPercentStyle = (rankPercent) => {
  if (rankPercent === null || rankPercent === undefined) return "text-gray-400";
  if (rankPercent >= 99) return "text-pink-400 font-bold";
  if (rankPercent >= 95) return "text-orange-400 font-bold";
  if (rankPercent >= 75) return "text-purple-400 font-semibold";
  if (rankPercent >= 50) return "text-blue-400 font-semibold";
  if (rankPercent >= 25) return "text-green-400";
  return "text-gray-300";
};

export const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return Math.round(amount).toLocaleString();
};

export const formatFightTime = (secondsIntoFight) => {
  if (secondsIntoFight === null || secondsIntoFight === undefined) return "—";
  const totalSeconds = Math.floor(secondsIntoFight);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const formatRankPercent = (rankPercent) => {
  if (rankPercent === null || rankPercent === undefined) return "Not yet ranked";
  return rankPercent.toFixed(1);
};

export const formatParsePercent = (parsePercent) => {
  if (parsePercent === null || parsePercent === undefined) return "—";
  return parsePercent.toFixed(0);
};

// Our own "parse": how a player's amount ranks against the other players in
// the same metric group (dps or healing) for this pull only — not a
// WarcraftLogs population percentile, just a within-pull comparison. Reuses
// getRankPercentStyle's color scale since it's the same 0-100 shape.
export const attachParsePercents = (rows) => {
  const ranked = rows
    .map((row, index) => ({ index, amount: row.amount }))
    .filter((row) => row.amount !== null && row.amount !== undefined)
    .sort((a, b) => b.amount - a.amount);

  const n = ranked.length;
  const parseByIndex = {};
  ranked.forEach((entry, rank) => {
    parseByIndex[entry.index] = n === 1 ? 100 : Math.round(((n - 1 - rank) / (n - 1)) * 100);
  });

  return rows.map((row, index) => ({
    ...row,
    parsePercent: parseByIndex[index] ?? null,
  }));
};

// Composite "Summary" parse: mostly the player's percentile in their main
// role's metric (dps or healing), plus a small nudge from their percentile in
// the other metric when they have one (e.g. a dps player's incidental
// healing, or a healer's incidental damage). Placeholder weighting — survivability
// and other signals will fold into this later.
export const combineParsePercents = (primary, secondary, secondaryWeight = 0.15) => {
  if (primary === null || primary === undefined) {
    return secondary === null || secondary === undefined ? null : secondary;
  }
  if (secondary === null || secondary === undefined) return primary;
  return Math.round(primary * (1 - secondaryWeight) + secondary * secondaryWeight);
};
